import {eventChannel} from 'redux-saga'
import {all, call, delay, fork, put, race, select, take, takeEvery} from 'redux-saga/effects';
import apiServer from '../../Api/Backend'
import {captureFromCamera, diffScore} from '../../Components/CapturePhotoDialog/useCapturePhoto'
import {AUPRED_PROMPT_FUNC} from '../../shared/common'
import {
  AUDIO_REDUCER,
  AUDIO_TRANSCRIBE_REQUEST,
  BOT_NAME,
  GENERAL_REDUCER,
  INITIATOR_AUPRED,
  NETWORK_SPEED,
  RECEIVE_TEXT_STREAM,
  RECEIVE_TEXT_STREAM_REFLECTED,
  SET_HIGHLIGHT_RANGE,
  STT_ENABLED,
  UPLOAD_MBPS,
  VIDEO_REDUCER
} from '../consts'
import {predict} from '../general/actions'
import {PREDICT, SET_END_OF_STREAM_REFLECTED} from '../general/consts'
import {
  setCapturedImage,
  setIsPhotoCaptureOpen,
  setPhotoCaptureWizardStep,
  setWhoIsInThePhoto,

} from './actions'
import {
  IS_CAMERA_ENABLED, CAPTURED_IMAGE,
  SAVE_PHOTO, SET_IS_CAMERA_ENABLED,
  START_PHOTOSHOOT, UPLOAD_SPEED_THRESHOLD_FOR_CAMERA,
} from './consts'


function* startPhotoshootSaga() {
  try {
    console.log('startPhotoshootSaga')
    yield put(setCapturedImage(null))
    yield put(setPhotoCaptureWizardStep(0))
    yield put(setWhoIsInThePhoto(''))
    yield put(setIsPhotoCaptureOpen(true))
  } catch (e) {
    console.log('error in startPhotoshootSaga', {e})
  }
}

function* savePhotoSaga({capturedImage, whoIsInThePhoto}) {
  try {

    yield call(apiServer.postWithFormData, '/savePhoto', {
      capturedImage,
      whoIsInThePhoto,
    })

    yield put(setIsPhotoCaptureOpen(false))

  } catch (e) {
    console.log('error in savePhotoSaga', {e})
  }
}


function createUserActivityChannel() {
  return eventChannel((emit) => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll']
    const handler = (e) => emit({ type: 'activity', eventType: e.type })

    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }))

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler))
    }
  })
}



function* idleAutoPredictSupervisor() {
  const IDLE_MS = 30_000 // photo prediction is 25 sec [with full history], we let the kv cache rebuild
  const SUSPEND_AUPRED_AFTER_AUPRED = 5_000
  const EXTRA_AUPRED_SUSPEND_AFTER_USER_PREDICTION_MS = 120_000
  const WAIT_FOR_STREAM_EVIDENCE_MS = 300_000

  const activityCh = yield call(createUserActivityChannel)

  const aupred_debug = (msg, extra = null) => {
    console.debug('[AuPred]', msg, extra ?? '')
  }


  try {
    aupred_debug('SUPERVISOR start; entering loop')
    const RANDOM_LOGGING_PROB = 0.05
    while (true) {

      const LLM_EVENTS = [
        PREDICT,
        RECEIVE_TEXT_STREAM,
        RECEIVE_TEXT_STREAM_REFLECTED,
        SET_END_OF_STREAM_REFLECTED,
        SET_IS_CAMERA_ENABLED,
        SET_HIGHLIGHT_RANGE,
      ]
      const sttEnabled = yield select(state => state.getIn([AUDIO_REDUCER, STT_ENABLED]))
      if (sttEnabled) {
        LLM_EVENTS.push(AUDIO_TRANSCRIBE_REQUEST)
        if (Math.random() < RANDOM_LOGGING_PROB/2) {
          aupred_debug(`STT enabled; will listen for ${AUDIO_TRANSCRIBE_REQUEST} event as evidence of activity (random logging)`)
        }
      } else {
        if (Math.random() < RANDOM_LOGGING_PROB/2) {
          aupred_debug(`STT disabled; if someone is speaking it doesnt prevent aupred (random logging)`)
        }
      }
      // aupred_debug('waiting-for-idle', { idleMs: IDLE_MS })

      const { timeout, activity, action } = yield race({
        timeout: delay(IDLE_MS),
        activity: take(activityCh),
        action: take(LLM_EVENTS),
      })

      if (activity) {
        if (Math.random() < RANDOM_LOGGING_PROB/2) {
          aupred_debug('user-activity (random logging)', activity)
        }
        continue
      }

      if (action) {
        if (action.type === PREDICT) {
          const textPrompt = typeof action.payload === 'string' ? action.payload : action.payload.content;
          if (action.initiator !== INITIATOR_AUPRED) {
            aupred_debug('human prompt detected; extra auPred suspension', { prompt: textPrompt.substring(0, 30) })
            yield delay(EXTRA_AUPRED_SUSPEND_AFTER_USER_PREDICTION_MS)
          }
        } if (action.type === AUDIO_TRANSCRIBE_REQUEST) {
            aupred_debug(`audio transcription request detected; treating as user activity that prevents aupred`)
        } else {
          if (Math.random() < RANDOM_LOGGING_PROB) {
            aupred_debug('llm-event (random logging)', {type: action.type})
          }
        }
      }

      if (!timeout) {
        if (Math.random() < RANDOM_LOGGING_PROB) {
          aupred_debug(`not idle yet; resetting timer (random logging)`)
        }
        continue
      }

      // --- TIMEOUT FIRED ---
      // aupred_debug('TIMEOUT fired; checking gates')

      const state = yield select()
      const cameraEnabled = state.getIn([VIDEO_REDUCER, IS_CAMERA_ENABLED])
      const uploadMbps = yield select(state => state.getIn([GENERAL_REDUCER,NETWORK_SPEED,UPLOAD_MBPS]))

      if (!cameraEnabled) {
        aupred_debug('ABORT camera-disabled')
        continue
      }
      if (uploadMbps <= UPLOAD_SPEED_THRESHOLD_FOR_CAMERA) {
        aupred_debug(`ABORT upload speed too slow for camera`, { uploadMbps, threshold: UPLOAD_SPEED_THRESHOLD_FOR_CAMERA })
        continue
      }

      const prevCapturedImage = state.getIn([VIDEO_REDUCER, CAPTURED_IMAGE])

      let imageUrl = null
      try {
        imageUrl = yield call(captureFromCamera)
      } catch (e) {
        aupred_debug('ABORT capture failed', String(e))
        continue
      }
      if (!imageUrl) {
        aupred_debug('ABORT capture returned empty')
        continue
      }

      yield put(setCapturedImage(imageUrl))

      if (!prevCapturedImage) {
        aupred_debug('BASELINE no prev image; not predicting')
        continue
      }

      let isSignificant = false
      try {
        const { score0to255, fracOverPxThreshold0to1 } = yield call(diffScore, prevCapturedImage, imageUrl)
        isSignificant = (score0to255 >= 6) || (fracOverPxThreshold0to1 >= 0.10)
        // aupred_debug('DIFF', { isSignificant, score0to255, fracOverPxThreshold0to1 })
      } catch (e) {
        aupred_debug('ABORT diff failed', String(e))
        continue
      }

      if (!isSignificant) {
        aupred_debug('Photo delta is not significant; will re-arm')
        continue
      } else {
        aupred_debug('Photo delta is significant; will predict')
      }
      const botName = yield select(state => state.getIn([GENERAL_REDUCER,BOT_NAME]))

      const prompt = {
        content: AUPRED_PROMPT_FUNC(botName),
        extra: [{ type: 'imageFile', base64Url: imageUrl }],
      }
      aupred_debug('DISPATCH idle prediction', { content: prompt.content, hasImage: true })
      yield put(predict({prompt, initiator:INITIATOR_AUPRED}))
      yield put(setIsPhotoCaptureOpen(false))
      // IMPORTANT: don’t immediately re-arm and spam.
      // Wait for *evidence* the stream is alive (token/reflected/eos),
      // or give up after 300s (rebuild kv cache takes time).
      console.log('gizim', 2902175, 'starting window in which aupred will not happen');
      const { evidence, evidenceTimeout } = yield race({
        evidence: take([RECEIVE_TEXT_STREAM, RECEIVE_TEXT_STREAM_REFLECTED, SET_END_OF_STREAM_REFLECTED]),
        evidenceTimeout: delay(WAIT_FOR_STREAM_EVIDENCE_MS),
      })
      yield delay(SUSPEND_AUPRED_AFTER_AUPRED)
      console.log('gizim', 8592558, 'ending window in which aupred will not happen');
    }
  } catch (e) {
    aupred_debug('SUPERVISOR error', e)
  } finally {
    aupred_debug('SUPERVISOR cleanup')
    activityCh.close()
  }
}


function* watchVideoSagas() {
  console.log('[idleSaga] watchVideoSagas START')
  yield takeEvery(START_PHOTOSHOOT, startPhotoshootSaga)
  yield takeEvery(SAVE_PHOTO, savePhotoSaga)
  yield fork(idleAutoPredictSupervisor)

}

export default function* videoSaga() {
  yield all([watchVideoSagas()])
}
